
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { apartments } from '../../mocks/apartments';
import AvailabilityBanner from './components/AvailabilityBanner';
import DateRangePicker from './components/DateRangePicker';

// Datas ocupadas (mesmas do DateRangePicker)
const BOOKED_RANGES: { start: string; end: string }[] = [
  { start: '2025-07-01', end: '2025-07-03' },
  { start: '2025-07-07', end: '2025-07-11' },
  { start: '2025-07-14', end: '2025-07-19' },
  { start: '2025-07-22', end: '2025-07-26' },
  { start: '2025-08-01', end: '2025-08-08' },
  { start: '2025-08-11', end: '2025-08-16' },
  { start: '2025-08-20', end: '2025-08-24' },
  { start: '2025-08-28', end: '2025-08-31' },
  { start: '2025-09-05', end: '2025-09-08' },
  { start: '2025-09-15', end: '2025-09-19' },
];

function strToDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isRangeAvailable(checkIn: string, checkOut: string): boolean {
  const start = strToDate(checkIn);
  const end = strToDate(checkOut);
  return !BOOKED_RANGES.some(({ start: bs, end: be }) => {
    const bookedStart = strToDate(bs);
    const bookedEnd = strToDate(be);
    return start < bookedEnd && end > bookedStart;
  });
}

function calcNights(checkIn: string, checkOut: string): number {
  const diff = strToDate(checkOut).getTime() - strToDate(checkIn).getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

function formatDatePT(dateStr: string): string {
  const d = strToDate(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export default function ApartamentosPage() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedCapacity, setSelectedCapacity] = useState<number>(0);
  const [checkIn, setCheckIn] = useState<string | null>(null);
  const [checkOut, setCheckOut] = useState<string | null>(null);

  const datesSelected = !!(checkIn && checkOut);
  const datesAvailable = datesSelected ? isRangeAvailable(checkIn!, checkOut!) : true;
  const nights = datesSelected ? calcNights(checkIn!, checkOut!) : 0;

  const filteredApartments = apartments.filter((apt) => {
    const typeMatch = selectedType === 'all' || apt.type.startsWith(selectedType);
    const capacityMatch = selectedCapacity === 0 || apt.capacity >= selectedCapacity;
    return typeMatch && capacityMatch;
  });

  function handleClearAll() {
    setSelectedType('all');
    setSelectedCapacity(0);
    setCheckIn(null);
    setCheckOut(null);
  }

  const hasActiveFilters = selectedType !== 'all' || selectedCapacity !== 0 || datesSelected;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/56fc9b76abdb21b5bf7172a6164db6ea.jpg"
            alt="Madeira"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Os Nossos Alojamentos
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto">
            Escolha o seu refúgio perfeito na Madeira
          </p>
        </div>
      </section>

      {/* Filters Section */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-wrap gap-6 items-end justify-center">

            {/* Date Range Picker */}
            <div className="relative">
              <DateRangePicker
                checkIn={checkIn}
                checkOut={checkOut}
                onChange={(ci, co) => { setCheckIn(ci); setCheckOut(co); }}
              />
            </div>

            {/* Divider */}
            <div className="hidden lg:block w-px h-12 bg-gray-200 self-center"></div>

            {/* Type Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Tipo de Apartamento</label>
              <div className="flex gap-2">
                {['all', 'T0', 'T2'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      selectedType === type
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type === 'all' ? 'Todos' : type === 'T0' ? 'T0 Studio' : 'T2 Premium'}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="hidden lg:block w-px h-12 bg-gray-200 self-center"></div>

            {/* Capacity Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Número de Hóspedes</label>
              <div className="flex gap-2">
                {[0, 2, 4].map((cap) => (
                  <button
                    key={cap}
                    onClick={() => setSelectedCapacity(cap)}
                    className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      selectedCapacity === cap
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cap === 0 ? 'Todos' : cap === 2 ? '2 Pessoas' : '4+ Pessoas'}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={handleClearAll}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-rose-500 transition-colors cursor-pointer whitespace-nowrap self-end pb-2"
              >
                <i className="ri-close-circle-line"></i>
                Limpar filtros
              </button>
            )}
          </div>

          {/* Results / Date Summary */}
          <div className="flex items-center justify-center gap-4 mt-4">
            {datesSelected && (
              <div className={`flex items-center gap-2 text-sm px-4 py-1.5 rounded-full font-medium ${
                datesAvailable
                  ? 'bg-teal-50 text-teal-700'
                  : 'bg-rose-50 text-rose-600'
              }`}>
                <i className={datesAvailable ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'}></i>
                {datesAvailable
                  ? `${formatDatePT(checkIn!)} → ${formatDatePT(checkOut!)} · ${nights} ${nights === 1 ? 'noite' : 'noites'} disponíveis`
                  : `Período de ${formatDatePT(checkIn!)} a ${formatDatePT(checkOut!)} tem datas ocupadas`
                }
              </div>
            )}
            <p className="text-gray-500 text-sm">
              {filteredApartments.length} {filteredApartments.length === 1 ? 'apartamento encontrado' : 'apartamentos encontrados'}
              {datesSelected && datesAvailable && ` · disponíveis para as suas datas`}
            </p>
          </div>
        </div>
      </section>

      {/* Availability Banner */}
      <AvailabilityBanner />

      {/* Apartments Grid */}
      <section className="flex-1 bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">

          {/* Date unavailable warning */}
          {datesSelected && !datesAvailable && (
            <div className="mb-8 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
              <div className="w-5 h-5 flex items-center justify-center text-amber-500 mt-0.5">
                <i className="ri-error-warning-line text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800">Período com disponibilidade limitada</p>
                <p className="text-sm text-amber-700 mt-0.5">
                  As datas selecionadas incluem períodos ocupados. Experimente ajustar as datas ou{' '}
                  <a
                    href={`https://wa.me/351962050526?text=Olá! Gostaria de verificar disponibilidade de ${formatDatePT(checkIn!)} a ${formatDatePT(checkOut!)}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="underline font-medium cursor-pointer"
                  >
                    contacte-nos via WhatsApp
                  </a>{' '}
                  para verificar opções.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-product-shop>
            {filteredApartments.map((apartment) => (
              <div
                key={apartment.id}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow group"
              >
                <div className="relative h-64 w-full overflow-hidden">
                  <img
                    src={apartment.image}
                    alt={apartment.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {apartment.featured && (
                    <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Destaque
                    </div>
                  )}
                  {datesSelected && datesAvailable && (
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-teal-700 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      <i className="ri-checkbox-circle-fill text-teal-500"></i>
                      Disponível
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{apartment.name}</h3>
                      <p className="text-sm text-gray-600">{apartment.type}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-teal-600">€{apartment.price}</div>
                      <div className="text-sm text-gray-600">/ noite</div>
                      {datesSelected && datesAvailable && nights > 0 && (
                        <div className="text-xs text-teal-600 font-medium mt-0.5">
                          €{apartment.price * nights} total
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mb-4 text-sm text-gray-700">
                    <div className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      <span>{apartment.capacity} pessoas</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-hotel-bed-line"></i>
                      <span>{apartment.beds} cama{apartment.beds > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-landscape-line"></i>
                      <span>{apartment.view}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {apartment.amenities.slice(0, 4).map((amenity, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded"
                      >
                        <i className={amenity.icon}></i>
                        <span>{amenity.name}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (checkIn) params.set('checkIn', checkIn);
                      if (checkOut) params.set('checkOut', checkOut);
                      const query = params.toString();
                      navigate(`/apartamento-${apartment.id}${query ? `?${query}` : ''}`);
                    }}
                    className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {datesSelected && datesAvailable ? 'Reservar estas Datas' : 'Ver Detalhes e Reservar'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* No Results */}
          {filteredApartments.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Nenhum apartamento encontrado
              </h3>
              <p className="text-gray-600 mb-6">
                Tente ajustar os filtros para ver mais opções
              </p>
              <button
                onClick={handleClearAll}
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Limpar Filtros
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
